/**
 * Percentage or flat amount off each ticket, with a live line showing what an
 * example ticket comes down to.
 */
import { TicketSalesCampaignDiscountType, type CampaignDiscount } from '@bonsai/shared';

import { cleanDiscountText } from '../../../utils/discount-amount-text';
import { SegmentedControl } from '../../dashboards/primitives/SegmentedControl';
import { EditorFieldError } from '../../shared/editor-page/EditorFieldError';
import { EditorFormSection } from '../../shared/editor-page/EditorFormSection';

import { CampaignAmountInput } from './CampaignAmountInput';
import { CampaignDiscountExample } from './CampaignDiscountExample';

export interface CampaignDiscountSectionProps {
  discountType: TicketSalesCampaignDiscountType;
  amount: string;
  /** The discount as entered, or null while the amount is not valid. */
  discount: CampaignDiscount | null;
  error?: string;
  onChange: (patch: { discountType?: TicketSalesCampaignDiscountType; amount?: string }) => void;
}

const TYPES = [
  { value: TicketSalesCampaignDiscountType.PERCENT, label: 'Percentage' },
  { value: TicketSalesCampaignDiscountType.FLAT, label: 'Flat amount' },
];

export function CampaignDiscountSection(p: CampaignDiscountSectionProps) {
  return (
    <EditorFormSection
      title="Discount"
      titleId="campaign-discount-title"
      hint="Applied to each ticket in a booking."
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <SegmentedControl
          ariaLabel="Discount type"
          value={p.discountType}
          options={TYPES}
          onChange={(type) =>
            p.onChange({ discountType: type, amount: cleanDiscountText(type, p.amount) })
          }
        />
        <CampaignAmountInput
          discountType={p.discountType}
          value={p.amount}
          error={p.error}
          onChange={(amount) => p.onChange({ amount })}
        />
        {p.discount && !p.error && <CampaignDiscountExample discount={p.discount} />}
      </div>
      {p.error && <EditorFieldError id="campaign-amount-error">{p.error}</EditorFieldError>}
    </EditorFormSection>
  );
}
